// =============================================================================
// selectors/economics.ts — the Economics tab, scope-enforced.
// -----------------------------------------------------------------------------
// ⚠ Same shape and same reason as `selectors/propReport.ts`: not a pass-through.
//
// ── 🚨 ONLY SOME OF THIS IS CLASS L, AND THE WHOLE THING TAKES A WholeBook ──
//
// The cash figures — payouts, fees, ROI, break-even, the pipeline, the firm
// table — read ACCOUNT ADJUSTMENTS, which arrive whole with the accounts. They
// are not scoped by the trade read at all and would be correct over any of it.
//
// Two things are not: `simPnl` sums trade P&L across every prop account over all
// time, and `economicsRebillRisk` needs each account's LAST trade date. Both are
// lifetime claims. A bounded read makes `simPnl` smaller than it is — and since
// Josh's is negative, smaller means BETTER — and makes an account look idle when
// it is not.
//
// So `computeEconomics` takes the `WholeBook` even though most of what it
// returns does not need one. The alternative is two entry points where the
// cheap one is the tempting one, and the number it would quietly get wrong is
// the one already labelled "deliberately excluded from every cash total" — the
// least-watched figure on the tab.
// =============================================================================

import {
  computeEconomics as computeEconomicsImpl,
  economicsByFirm as economicsByFirmImpl,
  economicsRebillRisk as economicsRebillRiskImpl,
  type Economics,
  type EconomicsAccount,
  type FirmRow,
  type RebillRisk,
} from '../domain/economics';
import type { AccountTrade } from '../domain/account';
import { isWholeBook, type WholeBook } from './population';

export { economicsAccounts, isPropCostBearing } from '../domain/economics';
export type {
  Economics, EconomicsAccount, FeeBreakdown, FirmRow, Pipeline, PayoutVelocity,
  RebillRisk, ResetStats,
} from '../domain/economics';

/**
 * Payout Velocity — re-exported UNWRAPPED, and the reason is worth stating
 * rather than leaving as an inconsistency beside the guarded wrappers below.
 *
 * ⚠ It reads no trades at all. Every figure comes from `Payout` and `Reset fee`
 * ADJUSTMENTS, which arrive whole on the account rows themselves — there is no
 * population to scope, so a `WholeBook` parameter here would be a type that
 * enforces nothing while implying it does. `selectors/patternVersions.ts` makes
 * the same call for the same reason.
 *
 * ⚠ That is only true while nothing on this card infers a payout from a trade.
 * If it ever does, it becomes Class L instantly — a bounded read would under-
 * count payouts and report a slower cadence than the trader actually has, which
 * is the optimistic direction on a card about how fast money arrives.
 */
export { payoutVelocity } from '../domain/economics';

function requireWholeBook<T>(p: WholeBook<T>, what: string): readonly T[] {
  if (!isWholeBook(p)) {
    throw new Error(
      `${what}: the population is not a whole book. Lifetime trade P&L and the idle-day ` +
        `count are functions of the entire history — from a partial read the P&L reads ` +
        `closer to zero than it is and a dormant account reads active. Block the surface.`,
    );
  }
  return p.rows;
}

export function computeEconomics<T extends AccountTrade>(
  accounts: readonly EconomicsAccount[],
  population: WholeBook<T>,
): Economics {
  return computeEconomicsImpl(accounts, requireWholeBook(population, 'computeEconomics'));
}

/** Grouping only — no population involved, so no scope to enforce. */
export function economicsByFirm(e: Economics): FirmRow[] {
  return economicsByFirmImpl(e);
}

export function economicsRebillRisk<T extends AccountTrade>(
  e: Economics, population: WholeBook<T>, todayIso: string,
): RebillRisk[] {
  return economicsRebillRiskImpl(
    e, requireWholeBook(population, 'economicsRebillRisk'), todayIso,
  );
}
